import { RefObject, useEffect, useState } from 'react';

export interface CarouselScrollMetrics {
	scrollLeft: number;
	scrollWidth: number;
	clientWidth: number;
}

export interface CarouselScrollState {
	hasOverflow: boolean;
	isStartReached: boolean;
	isEndReached: boolean;
}
const SCROLL_TOLERANCE = 1;

export const computeCarouselScrollState = ({
	scrollLeft,
	scrollWidth,
	clientWidth,
}: CarouselScrollMetrics): CarouselScrollState => {
	const hasOverflow = scrollWidth - clientWidth > SCROLL_TOLERANCE;
	return {
		hasOverflow,
		isStartReached: !hasOverflow || scrollLeft <= SCROLL_TOLERANCE,
		isEndReached: !hasOverflow || scrollLeft + clientWidth >= scrollWidth - SCROLL_TOLERANCE,
	};
};

const INITIAL_STATE: CarouselScrollState = {
	hasOverflow: false,
	isStartReached: true,
	isEndReached: true,
};

export const useCarouselScrollState = (
	ref: RefObject<HTMLElement>,
	slidesLength: number
): CarouselScrollState => {
	const [state, setState] = useState<CarouselScrollState>(INITIAL_STATE);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const update = () => setState(computeCarouselScrollState(element));

		update();

		element.addEventListener('scroll', update, { passive: true });

		const resizeObserver =
			typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : undefined;
		resizeObserver?.observe(element);

		window.addEventListener('resize', update);

		return () => {
			element.removeEventListener('scroll', update);
			resizeObserver?.disconnect();
			window.removeEventListener('resize', update);
		};
	}, [ref, slidesLength]);

	return state;
};
